import { useCallback, useEffect, useId, useSyncExternalStore } from "react";

const membersByKey = new Map<string, string[]>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * Elects one mounted instance per `key` as the leader.
 *
 * React Query gives every observer its own `refetchInterval` timer, and those
 * fetches are only deduped while one is in flight. A hook mounted N times on a
 * page therefore polls N times per interval. Gate the interval (and other
 * per-instance side effects such as reconnect refetches) on this flag so only
 * one instance does the work. When the leader unmounts, the next-oldest
 * instance takes over.
 *
 * Returns `false` for every instance until effects have run once.
 *
 * @example
 * const isLeader = usePollingLeader("integrations");
 * useQuery({ ..., refetchInterval: isLeader ? usePollingGate(5000) : false });
 */
export const usePollingLeader = (key: string): boolean => {
  const id = useId();

  useEffect(() => {
    membersByKey.set(key, [...(membersByKey.get(key) ?? []), id]);
    notify();
    return () => {
      const remaining = (membersByKey.get(key) ?? []).filter((m) => m !== id);
      if (remaining.length) membersByKey.set(key, remaining);
      else membersByKey.delete(key);
      notify();
    };
  }, [key, id]);

  const getSnapshot = useCallback(
    () => membersByKey.get(key)?.[0] === id,
    [key, id],
  );

  return useSyncExternalStore(subscribe, getSnapshot, () => false);
};
